import { useEffect, useRef, useContext } from 'react';
import { useApp, AppContext } from '@/context/AppContext';
import { useToast } from '@/hooks/use-toast';
import { Sparkles, Package, Egg, TestTube2 } from 'lucide-react';

export function ItemDropNotifications() {
  const context = useContext(AppContext);
  
  // Return early if context is not available
  if (!context) {
    return null;
  }
  
  const { state } = context;
  const { toast } = useToast();
  // Initialize ref with empty array to ensure consistent hook calls
  const previousInventoryRef = useRef<any[]>([]);

  useEffect(() => {
    // Don't process notifications if not authenticated or no pet collection
    if (!state.isAuthenticated || !state.petCollection?.inventory) {
      return;
    }

    const currentInventory = state.petCollection.inventory;
    const previousInventory = previousInventoryRef.current;

    // Check for new items (items that weren't in previous inventory or have higher quantity)
    const newDrops: Array<{type: string; key: string; quantity: number}> = [];
    
    currentInventory.forEach(currentItem => {
      const previousItem = previousInventory.find(
        prev => prev.item_type === currentItem.item_type && prev.item_key === currentItem.item_key
      );
      
      if (!previousItem) {
        // Completely new item
        newDrops.push({
          type: currentItem.item_type,
          key: currentItem.item_key,
          quantity: currentItem.quantity
        });
      } else if (currentItem.quantity > previousItem.quantity) {
        // Increased quantity
        newDrops.push({
          type: currentItem.item_type,
          key: currentItem.item_key,
          quantity: currentItem.quantity - previousItem.quantity
        });
      }
    });

    // Show notification for drops
    if (newDrops.length > 0) {
      const getItemIcon = (type: string) => {
        switch (type) {
          case 'egg': return <Egg className="w-4 h-4 text-blue-500" />;
          case 'potion': return <TestTube2 className="w-4 h-4 text-purple-500" />;
          case 'food': return <Package className="w-4 h-4 text-green-500" />;
          default: return <Package className="w-4 h-4" />;
        }
      };

      const getItemName = (type: string, key: string) => {
        const names: Record<string, Record<string, string>> = {
          egg: { wolf: 'Wolf Egg', tiger: 'Tiger Egg', dragon: 'Dragon Egg', fox: 'Fox Egg', panda: 'Panda Egg' },
          potion: { base: 'Base Potion', red: 'Red Potion', golden: 'Golden Potion', white: 'White Potion' },
          food: { 
            basic_seed: 'Basic Seed', 
            premium_berry: 'Premium Berry', 
            energy_nectar: 'Energy Nectar',
            golden_apple: 'Golden Apple',
            magical_treat: 'Magical Treat'
          }
        };
        return names[type]?.[key] || `${key} ${type}`;
      };

      toast({
        title: "🎉 Items Found!",
        description: (
          <div className="space-y-2">
            <p className="text-sm">Activity reward drops:</p>
            <div className="space-y-1">
              {newDrops.map((drop, index) => (
                <div key={index} className="flex items-center gap-2">
                  {getItemIcon(drop.type)}
                  <span className="text-sm">
                    {getItemName(drop.type, drop.key)} x{drop.quantity}
                  </span>
                  <Sparkles className="w-3 h-3 text-yellow-500" />
                </div>
              ))}
            </div>
          </div>
        ),
        duration: 5000
      });
    }

    // Update ref for next comparison
    previousInventoryRef.current = [...currentInventory];
  }, [state.petCollection?.inventory, toast, state.isAuthenticated]);

  return null; // This component doesn't render anything
}